// DayPartIconButton.tsx
import React from "react";
import styles from "./DayPartIconButton.module.css";
import { DayPart } from "../../gql/types"; // adjust import path
import { ReactComponent as EllipsisIcon } from "../../assets/icons/surroundingIcon.svg";
import { ReactComponent as WhiteEllipsisIcon } from "../../assets/icons/whiteSurroundingIcon.svg";
import morningIcon from "../../assets/icons/morningIcon.svg";
import earlyAfternoonIcon from "../../assets/icons/earlyAfternoonButton.svg";
import lateAfternoonIcon from "../../assets/icons/lateAfternoonIcon.svg";
import eveningIcon from "../../assets/icons/eveningIcon.svg";
import whiteMorningIcon from "../../assets/icons/whiteMorningIcon.svg";
import whiteEarlyAfternoonIcon from "../../assets/icons/whiteEarlyAfternoonIcon.svg";
import whiteLateAfternoonIcon from "../../assets/icons/whiteLateAfternoonIcon.svg";
import whiteEveningIcon from "../../assets/icons/whiteEveningIcon.svg";

import { RecommendationStatus } from "./types";

// A helper for rendering the actual daypart icon
function renderDayPartIcon(dp: DayPart, readOnly: boolean, useWhite: boolean) {
    if (!readOnly && !useWhite) {
        switch (dp) {
            case DayPart.Morning:
                return <img src={morningIcon} />;
            case DayPart.EarlyAfternoon:
                return <img src={earlyAfternoonIcon} />;
            case DayPart.LateAfternoon:
                return <img src={lateAfternoonIcon} />;
            case DayPart.Evening:
                return <img src={eveningIcon} />;
            default:
                return null;
        }
    }

    switch (dp) {
        case DayPart.Morning:
            return <img src={whiteMorningIcon} />;
        case DayPart.EarlyAfternoon:
            return <img src={whiteEarlyAfternoonIcon} />;
        case DayPart.LateAfternoon:
            return <img src={whiteLateAfternoonIcon} />;
        case DayPart.Evening:
            return <img src={whiteEveningIcon} />;
        default:
            return null;
    }
}

interface DayPartIconButtonProps {
    /** Whether this daypart is currently selected (for styling) */
    selected: boolean;
    /** Click handler (e.g. toggling selection) */
    onClick: () => void;
    /** The daypart to display */
    dayPart: DayPart;
    /** Optional banner text (e.g. "Most Popular") */
    readOnly?: boolean;
    bannerText?: string;
    recommendation?: RecommendationStatus;
    useTransparentStyling?: boolean;
}

/**
 * A reusable button that displays:
 * - An ellipsis icon (as a background frame)
 * - The daypart icon on top
 * - An optional banner floating above or below
 */
const DayPartIconButton: React.FC<DayPartIconButtonProps> = ({
    selected,
    onClick,
    dayPart,
    bannerText,
    readOnly = false,
    recommendation = RecommendationStatus.Neutral,
    useTransparentStyling = false,
}) => {
    const recommendationClass =
        recommendation === RecommendationStatus.Recommended
            ? styles.recommended
            : recommendation === RecommendationStatus.NotRecommended
                ? styles.notRecommended
                : styles.neutral;

    const btnClassName = `${styles.dayPartButton}
        ${selected ? styles.selected : ""}
        ${recommendationClass}
        ${readOnly ? (selected ? styles.readOnlySelectedBackground : styles.readOnlyBackground) : ""}
        ${useTransparentStyling ? styles.transparentStyling : ""}
    `;

    return (
        <div className={styles.dayPartContainer}>
            <button
                type="button"
                onClick={onClick}
                className={btnClassName}
                disabled={readOnly}
            >
                <span className={styles.ellipsisWrapper}>
                    {useTransparentStyling
                        ? <WhiteEllipsisIcon className={styles.ellipsisIcon} />
                        : <EllipsisIcon className={styles.ellipsisIcon} />}
                    <span className={styles.innerDayPartIcon}>
                        {renderDayPartIcon(dayPart, readOnly && selected, useTransparentStyling)}
                    </span>
                </span>
            </button>

            {bannerText && (
                <div className={styles.bannerText}>
                    {bannerText}
                </div>
            )}
        </div>
    );
};

export default DayPartIconButton;
